import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { CONTRACT_TYPES_DICTIONARY_QUERY, PAGED_FINANCIAL_PRODUCTS_QUERY } from '../queries';
import { Card } from '@totalsoft/rocket-ui';
import { CollectionType, getListBrowserStore } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useMemo } from 'react';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import FinancialProductListItem from './FinancialProductListItem';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import FinancialProductStateHistoryModalContainer from './FinancialProductStateHistoryModalContainer';
import { useMutation } from 'hooks/apollo/useMutation';
import { DUPLICATE_FINANCIAL_PRODUCT, REMOVE_FINANCIAL_PRODUCT } from '../mutations';
import { FINANCIAL_PRODUCT_DUPLICATED, FINANCIAL_PRODUCT_REMOVED } from '../subscriptions';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import useSubscription from 'hooks/apollo/useSubscription';

export const financialProductsStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'productName',
      caption: 'FinancialProduct.ProductName',
      type: 'text'
    },
    {
      name: 'productCode',
      caption: 'FinancialProduct.ProductCode',
      type: 'text'
    },
    {
      name: 'leasingTypeId',
      caption: 'FinancialProduct.LeasingTypeName',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'isActive',
      caption: 'FinancialProduct.IsActive',
      type: 'boolean'
    }
  ],
  orderByFields: [
    {
      caption: 'FinancialProduct.ProductName',
      value: 'ProductName'
    },
    {
      caption: 'FinancialProduct.ProductCode',
      value: 'ProductCode'
    }
  ],
  pager: {
    orderBy: 'ProductName',
    direction: 'asc'
  }
});

const FinancialProductListBrowser = ({ canWrite }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const historyContext = useHistoryWithContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productId, setProductId] = useState(null);

  const financialProductsFetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_FINANCIAL_PRODUCTS_QUERY,
        name: 'pagedFinancialProducts'
      },
      collectionsOptions: [
        {
          queryOptions: { query: CONTRACT_TYPES_DICTIONARY_QUERY },
          name: 'contractTypesDictionary',
          identifier: 'leasingTypeId',
          collectionType: CollectionType.Filter,
          valueKey: 'contractTypeId',
          labelKey: 'contractTypeName'
        }
      ]
    }),
    []
  );

  // #region Actions

  const [deleteFinancialProduct] = useMutation(REMOVE_FINANCIAL_PRODUCT);

  const [duplicateFinancialProduct] = useMutation(DUPLICATE_FINANCIAL_PRODUCT);

  useSubscription(FINANCIAL_PRODUCT_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.FinancialProductRemoved'), 'success');
      client.cache.evict({
        id: `FinancialProductItem:{"productId":${data.financialProductDeleted.productId}}`
      });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedFinancialProducts' });
      client.cache.gc();
    }
  });

  useSubscription(FINANCIAL_PRODUCT_DUPLICATED, {
    onData: ({ data: { data } }) => {
      historyContext.push(`/financialProducts/${data.financialProductDuplicated.productId}`);
    }
  });

  // #endregion

  // #region Handlers

  const handleEdit = useCallback(
    id => () => {
      historyContext.push(`/financialProducts/${id}`);
    },
    [historyContext]
  );

  const handleDelete = useCallback(
    id => () => {
      if (window.confirm(t('FinancialProduct.DeleteFinancialProduct'))) {
        deleteFinancialProduct({
          variables: {
            input: { productId: id }
          }
        });
      }
    },
    [deleteFinancialProduct, t]
  );

  const handleDuplicate = useCallback(
    id => () => {
      if (window.confirm(t('FinancialProduct.DuplicateFinancialProduct'))) {
        duplicateFinancialProduct({
          variables: {
            input: { productId: id }
          }
        });
      }
    },
    [duplicateFinancialProduct, t]
  );

  const handleShowStateHistory = useCallback(
    id => () => {
      setProductId(id);
      setIsModalOpen(true);
    },
    []
  );

  // #endregion

  const columns = useMemo(
    () => [
      { name: 'productName', caption: 'FinancialProduct.ProductName' },
      { name: 'productCode', caption: 'FinancialProduct.ProductCode', width: 8 },
      { name: 'leasingTypeName', caption: 'FinancialProduct.LeasingTypeName', width: 8 },
      { name: 'validFrom', caption: 'FinancialProduct.ValidFrom', width: 10 },
      { name: 'validTo', caption: 'FinancialProduct.ValidTo', width: 10 },
      { name: 'productTypeName', caption: 'FinancialProduct.ProductTypeName' },
      { name: 'description', caption: 'FinancialProduct.Description' },
      { name: 'isActive', caption: 'FinancialProduct.IsActive', width: 8 },
      { name: 'stateName', caption: 'FinancialProduct.StateName', width: 8 },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  const itemComponent = useCallback(
    ({ data }) => (
      <FinancialProductListItem
        financialProduct={data}
        canWrite={canWrite}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        onShowStateHistory={handleShowStateHistory}
      />
    ),
    [canWrite, handleEdit, handleDelete, handleDuplicate, handleShowStateHistory]
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser
        name='financial-products-browser'
        store={financialProductsStore}
        ItemComponent={itemComponent}
        columns={columns}
        layout='fixed'
      />
      <ExportModalComponent store={financialProductsStore} options={financialProductsFetchingOptions} />
      <ApolloFetchingComponent store={financialProductsStore} options={financialProductsFetchingOptions} />
      {isModalOpen && productId ? (
        <FinancialProductStateHistoryModalContainer
          productId={productId}
          closeModal={() => {
            setIsModalOpen(false);
          }}
        />
      ) : (
        <></>
      )}
    </Card>
  );
};

FinancialProductListBrowser.propTypes = {
  canWrite: PropTypes.bool.isRequired
};

export default FinancialProductListBrowser;
